import {ChangeEvent, useContext, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Checkbox, Grid, SxProps, Typography} from '@mui/material';

import {ReactComponent as CheckCircleIcon} from '../../../../../../assets/inputs/check-circle.svg';
import {ReactComponent as CircleIcon} from '../../../../../../assets/inputs/circle.svg';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';
import {EmpionRecommendedActionsService} from '../../services/EmpionRecommendedActionsService';
import {EmpionRecommendation} from '../../types/EmpionRecommendationsTypes';

interface EmpionRecommendedActionsProps {
  empionRecommendedActions: EmpionRecommendation[];
}

export default function EmpionRecommendedActions({empionRecommendedActions}: EmpionRecommendedActionsProps) {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);

  const [checked, setChecked] = useState<Record<string, boolean>>({});

  const recomendedActionsService = useMemo(() => new EmpionRecommendedActionsService(), []);

  useEffect(() => {
    const initialCheckedState: Record<string, boolean> = {};

    empionRecommendedActions.forEach((recommendation, index) => {
      recommendation.actionItems.forEach((actionItem, actionIndex) => {
        const checkboxIndex = `${index}-${actionIndex}`;
        initialCheckedState[checkboxIndex] = actionItem.completedAt !== null;
      });
    });

    setChecked(initialCheckedState);
  }, [empionRecommendedActions]);

  const handleChange = (id: string, checkboxIndex: string) => async (event: ChangeEvent<HTMLInputElement>) => {
    const newCheckedState = event.target.checked;
    const response = await recomendedActionsService.toggleActionItems(id);
    if (response.ok) {
      setChecked((prevChecked) => ({
        ...prevChecked,
        [checkboxIndex]: newCheckedState,
      }));
    } else {
      console.error('Error toggling action items');
      dispatchMessage(t('shared.formSubmit.error'), 'error');
    }
  };

  const paperStyles: SxProps = {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    padding: 4,
    backgroundColor: 'white',
    boxShadow: 'none',
    border: `1px solid ${orangeColors[3]}`,
    width: '100%',
    borderRadius: '16px',
  };

  const renderEmpionRecommendedActions = () => {
    return empionRecommendedActions.map((recommendation, index) => {
      return (
        <Grid container sx={paperStyles} key={index}>
          <Box
            bgcolor={greenColors[700]}
            borderRadius={'50%'}
            height={30}
            width={30}
            mb={4}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Typography variant='body1' fontWeight={700} color={'white'} lineHeight={1}>
              {index + 1}
            </Typography>
          </Box>
          <Typography variant='body1' mb={4}>
            {recommendation.recommendationText[currentLanguage]}
          </Typography>

          <Box
            sx={{
              backgroundColor: orangeColors[3],
              padding: '8px 16px 8px 16px',
              borderRadius: 2,
              width: '100%',
              display: recommendation.actionItems.length > 0 ? 'block' : 'none',
            }}
          >
            {recommendation.actionItems.map((actionItem, actionIndex) => {
              const checkboxIndex = `${index}-${actionIndex}`;
              return (
                <Box display='flex' alignItems={'flex-start'} key={checkboxIndex}>
                  <Checkbox
                    name={`checkbox-${checkboxIndex}`}
                    checked={checked[checkboxIndex] || false}
                    onChange={handleChange(actionItem.resourceId, checkboxIndex)}
                    icon={<CircleIcon fill={greenColors[100]} />}
                    checkedIcon={<CheckCircleIcon fill={greenColors[100]} />}
                  />
                  <Typography variant='body1' color={greenColors[100]} marginTop='3px'>
                    {actionItem.actionItemText[currentLanguage]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Grid>
      );
    });
  };

  return (
    <Grid container mt={8} mb={2} sx={{flexGrow: 1}}>
      <Typography variant='h2' mb={2}>
        {t('companyCultureDashboard.recommendedActionsTitle')}
      </Typography>
      <Grid
        item
        container
        gap={4}
        display={'grid'}
        gridTemplateColumns={{xs: '1fr', md: 'repeat(2, minmax(min-content, 1fr))'}}
      >
        {renderEmpionRecommendedActions()}
      </Grid>
    </Grid>
  );
}
